// VIDEO-EDITING — transient store for Marcus's b-roll cutaway proposals.
//
// `propose_video_broll` (backend dry-run sibling of `apply_video_broll`)
// returns a normalized list of cutaway moments with stock candidates
// and AI prompts but does not persist anything. `useCompanionToolDispatch`
// lifts that result here so `MarcusBrollSuggestionsPanel` can render
// proposal cards for the user to accept / edit / discard before a
// follow-up `apply_video_broll` commits the approved subset as
// kind:'broll' clips with sourceAudioMode:'continue' (narration keeps
// playing under the cutaway visual).
//
// State machine (mirrors useOverlayProposalsStore):
//   idle ──Generate──► generating ──propose result──► proposed
//                          │                            │
//                          └── 90s timeout ──► error    │ Apply
//                                                       ▼
//                                                   applying
//                                          upsert result │
//                                                       ▼
//                                                    applied (10s)
//                                                       │ auto
//                                                       ▼
//                                                     idle ◄── Undo (60s)
//
// Intentionally NOT persisted — proposals + lastApplied are session-scoped.
import { create } from "zustand";

import type {
  BrollProposal,
  BrollProposalDraft,
  BrollProposalSource,
  BrollProposalsStatus,
  BrollAppliedSnapshot,
} from "../types/brollProposal";

function makeDraft(proposal: BrollProposal): BrollProposalDraft {
  return {
    ...proposal,
    selectedSource: "generative",
    selectedStockIndex: 0,
    uploadClipUrl: null,
  };
}

interface BrollProposalsState {
  status: BrollProposalsStatus;
  projectId: string | null;
  sourceActionId: string | null;
  proposals: BrollProposalDraft[];
  durationSec: number | null;
  productRefAttached: boolean;
  error: string | null;
  lastApplied: BrollAppliedSnapshot | null;

  /** Status-discriminated mutators (mirror useOverlayProposalsStore). */
  beginGenerating: (projectId: string) => void;
  setProposals: (input: {
    projectId: string;
    sourceActionId: string | null;
    proposals: BrollProposal[];
    durationSec: number | null;
    productRefAttached: boolean;
  }) => void;
  setError: (projectId: string, error: string) => void;
  updateProposal: (proposalId: string, patch: Partial<BrollProposalDraft>) => void;
  removeProposal: (proposalId: string) => void;
  setProposalSource: (proposalId: string, source: BrollProposalSource) => void;
  setProposalStockIndex: (proposalId: string, index: number) => void;
  clearProposals: () => void;

  beginApplying: (input: { projectId: string }) => void;
  markApplied: (input: {
    projectId: string;
    sourceActionId: string;
    appliedClips: BrollAppliedSnapshot["appliedClips"];
    replacedIds: string[];
  }) => void;
  markApplyError: (projectId: string, error: string) => void;
  consumeLastApplied: () => BrollAppliedSnapshot | null;
  clearLastApplied: () => void;
}

export const useBrollProposalsStore = create<BrollProposalsState>((set, get) => ({
  status: "idle",
  projectId: null,
  sourceActionId: null,
  proposals: [],
  durationSec: null,
  productRefAttached: false,
  error: null,
  lastApplied: null,

  beginGenerating: (projectId) =>
    set({
      status: "generating",
      projectId,
      proposals: [],
      error: null,
      // Starting a new propose run invalidates the prior undo window —
      // once we surface fresh proposals the snapshot is stale.
      lastApplied: null,
    }),

  setProposals: ({ projectId, sourceActionId, proposals, durationSec, productRefAttached }) =>
    set({
      status: proposals.length > 0 ? "proposed" : "idle",
      projectId,
      sourceActionId,
      proposals: proposals.map(makeDraft),
      durationSec,
      productRefAttached,
      error: null,
    }),

  setError: (projectId, error) =>
    set({
      status: "error",
      projectId,
      proposals: [],
      error,
    }),

  updateProposal: (proposalId, patch) =>
    set((state) => ({
      proposals: state.proposals.map((proposal) =>
        proposal.proposalId === proposalId ? { ...proposal, ...patch } : proposal,
      ),
    })),

  removeProposal: (proposalId) =>
    set((state) => {
      const next = state.proposals.filter((p) => p.proposalId !== proposalId);
      return {
        proposals: next,
        status: next.length === 0 ? "idle" : state.status,
      };
    }),

  setProposalSource: (proposalId, source) =>
    set((state) => ({
      proposals: state.proposals.map((p) =>
        p.proposalId === proposalId ? { ...p, selectedSource: source } : p,
      ),
    })),

  setProposalStockIndex: (proposalId, index) =>
    set((state) => ({
      proposals: state.proposals.map((p) =>
        p.proposalId === proposalId ? { ...p, selectedStockIndex: index } : p,
      ),
    })),

  clearProposals: () =>
    set({
      status: "idle",
      proposals: [],
      error: null,
    }),

  beginApplying: ({ projectId }) =>
    set({
      status: "applying",
      projectId,
    }),

  markApplied: ({ projectId, sourceActionId, appliedClips, replacedIds }) =>
    set({
      status: "applied",
      projectId,
      sourceActionId,
      proposals: [],
      error: null,
      lastApplied: {
        projectId,
        sourceActionId,
        appliedClips,
        replacedIds,
        at: Date.now(),
      },
    }),

  markApplyError: (projectId, error) =>
    set({
      status: "error",
      projectId,
      error,
    }),

  consumeLastApplied: () => {
    const snapshot = get().lastApplied;
    if (snapshot) set({ lastApplied: null, status: "idle" });
    return snapshot;
  },

  clearLastApplied: () => set({ lastApplied: null }),
}));
